import React, { useRef, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon, File as FileIcon } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const TYPES = ["Contratto", "Logo", "Altro"];
const TYPE_LABEL = { Contratto: "Contratto", Logo: "Logo", Altro: "Altro" };

// Match del backend `ALLOWED_MIME` in /app/backend/routes/internal_documenti.py.
const ACCEPT =
    ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.csv,.zip," +
    "application/pdf,image/jpeg,image/png,image/webp," +
    "application/msword," +
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
    "application/vnd.ms-excel," +
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
    "text/csv," +
    "application/zip,application/x-zip-compressed";

const MAX_BYTES = 20 * 1024 * 1024;

function typeIcon(t) {
    if (t === "Contratto") return <FileText size={13} strokeWidth={2} />;
    if (t === "Logo") return <ImageIcon size={13} strokeWidth={2} />;
    return <FileIcon size={13} strokeWidth={2} />;
}

function fmtSize(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Modal di upload documento pre-scoped su un cliente.
 * Riusa POST /api/internal/documenti (multipart) — nessun nuovo endpoint backend.
 * Il campo `client_id` è fissato dal contesto (scheda cliente), non modificabile dall'utente.
 */
export default function UploadDocumentModal({ open, onClose, clientId, clientName, onUploaded }) {
    const [name, setName] = useState("");
    const [type, setType] = useState("Contratto");
    const [visible, setVisible] = useState(true);
    const [notes, setNotes] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    if (!open) return null;

    const reset = () => {
        setName(""); setType("Contratto"); setVisible(true);
        setNotes(""); setFile(null); setSaving(false); setError(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleClose = () => {
        if (saving) return;
        reset();
        onClose?.();
    };

    const onPick = () => inputRef.current?.click();

    const onFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_BYTES) {
            setError("File troppo grande (max 20 MB)");
            return;
        }
        setError(null);
        setFile(f);
        if (!name) setName(f.name.replace(/\.[^.]+$/, ""));
    };

    const canSubmit = !!clientId && !!file && name.trim().length >= 2 && !!type && !saving;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        setError(null);
        try {
            const fd = new FormData();
            fd.append("client_id", clientId);
            fd.append("name", name.trim());
            fd.append("type", type);
            fd.append("visible_to_client", String(visible));
            if (notes.trim()) fd.append("notes", notes.trim());
            fd.append("file", file);
            const { data } = await api.post("/internal/documenti", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Documento caricato");
            // Mitigazione A ZIP: info toast dedicato se backend ritorna warning
            if (data?.warning_message) {
                toast.info(data.warning_message, { duration: 5000 });
            }
            onUploaded?.();
            reset();
            onClose?.();
        } catch (err) {
            setError(err?.response?.data?.detail || "Errore caricamento");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="lt-modal-backdrop" onClick={handleClose} data-testid="client-upload-doc-modal">
            <div className="lt-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
                <div className="lt-modal__head">
                    <h2 className="lt-modal__title">Carica documento</h2>
                    <button className="lt-icon-btn" onClick={handleClose} aria-label="Chiudi" disabled={saving}
                        data-testid="client-upload-doc-close">
                        <X size={16} />
                    </button>
                </div>

                <form onSubmit={submit} className="lt-scad-form" data-testid="client-upload-doc-form">
                    <div style={{
                        padding: "10px 12px",
                        marginBottom: 12,
                        background: "#FAFAF7",
                        border: "1px solid #EEEFEA",
                        borderRadius: 4,
                    }}>
                        <div style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 11,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                            color: "#5F6560",
                            marginBottom: 4,
                        }} data-testid="client-upload-doc-eyebrow">
                            Per: {clientName}
                        </div>
                        <div style={{ fontSize: 12, color: "#8A8F88" }}>
                            Il documento sarà associato a questo cliente.
                        </div>
                    </div>

                    <div className="lt-form-row">
                        <label className="lt-label">Nome documento *</label>
                        <input
                            className="lt-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            minLength={2}
                            maxLength={200}
                            data-testid="client-upload-doc-name"
                        />
                    </div>

                    <div className="lt-form-row">
                        <label className="lt-label">Tipo *</label>
                        <div className="lt-radio-row" data-testid="client-upload-doc-type">
                            {TYPES.map((t) => (
                                <button
                                    key={t}
                                    type="button"
                                    className={"lt-radio-chip" + (type === t ? " is-active" : "")}
                                    onClick={() => setType(t)}
                                    data-testid={`client-upload-doc-type-${t}`}
                                >
                                    {typeIcon(t)} {TYPE_LABEL[t]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lt-form-row">
                        <label className="lt-label">File *</label>
                        <div className="lt-file-picker">
                            <input
                                ref={inputRef}
                                type="file"
                                accept={ACCEPT}
                                onChange={onFile}
                                style={{ display: "none" }}
                                data-testid="client-upload-doc-file"
                            />
                            <button type="button" className="lt-btn-secondary" onClick={onPick}>
                                <Upload size={14} /> Scegli file
                            </button>
                            <span className="lt-file-name">
                                {file ? `${file.name} · ${fmtSize(file.size)}` : "Nessun file selezionato (max 20 MB)"}
                            </span>
                        </div>
                    </div>

                    <div className="lt-form-row">
                        <label className="lt-label">Note (opzionale)</label>
                        <textarea
                            className="lt-textarea"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            maxLength={2000}
                            data-testid="client-upload-doc-notes"
                        />
                    </div>

                    <div className="lt-toggle-row">
                        <label className="lt-toggle">
                            <input
                                type="checkbox"
                                checked={visible}
                                onChange={(e) => setVisible(e.target.checked)}
                                data-testid="client-upload-doc-vis"
                            />
                            <span className="lt-toggle__track"><span className="lt-toggle__thumb" /></span>
                            <span className="lt-toggle__label">Visibile al cliente</span>
                        </label>
                        <p className="lt-toggle__hint">Quando attivo, il documento appare nel portale del cliente.</p>
                    </div>

                    {error && (
                        <div style={{
                            marginTop: 4,
                            padding: "10px 12px",
                            fontSize: 13,
                            background: "rgba(220, 38, 38, 0.08)",
                            border: "1px solid rgba(220, 38, 38, 0.25)",
                            borderRadius: 4,
                            color: "#DC2626",
                        }} data-testid="client-upload-doc-error">
                            {error}
                        </div>
                    )}

                    <div className="lt-form-actions">
                        <div style={{ flex: 1 }} />
                        <button
                            type="button"
                            className="lt-btn-secondary"
                            onClick={handleClose}
                            disabled={saving}
                            data-testid="client-upload-doc-cancel"
                        >
                            Annulla
                        </button>
                        <button
                            type="submit"
                            className="lt-btn-primary"
                            disabled={!canSubmit}
                            data-testid="client-upload-doc-submit"
                        >
                            {saving ? "Caricamento…" : "Carica"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
